import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Check,
  CheckCircle2,
  ChevronRight,
  FastForward,
  Lock,
  Pause,
  Play,
  Rewind,
  RotateCcw,
} from 'lucide-react';
import { motion, useReducedMotion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  answerPublicDemoQuestion,
  createInitialPublicDemoState,
  getPublicDemoExercises,
  persistPublicDemoState,
  restorePublicDemoState,
} from '../lib/publicDemo';
import {
  trackDemoCompleted,
  trackDemoProgress,
  trackDemoStarted,
} from '../services/analyticsService';

const DEMO_COPY = {
  en: {
    eyebrow: 'Free mini-lesson',
    steps: ['Listen', 'Quick check', 'Your result'],
    lessonProgress: 'Lesson progress',
    listenPrompt: 'Listen to the episode. Once you reach 75%, the quick check unlocks.',
    continue: 'Continue to the quick check',
    keepListening: 'Keep listening to unlock',
    question: 'Question',
    chooseAnswer: 'Choose one answer',
    nextQuestion: 'Next question',
    seeResult: 'See result',
    resultEyebrow: 'Mini-lesson complete',
    resultTitle: 'You turned listening into learning.',
    resultBody: 'Your progress is ready to save. Create a free account to unlock the next lesson at your level.',
    score: 'correct',
    saveProgress: 'Save your progress and unlock the next lesson',
    listenAgain: 'Listen again',
    playbackSpeed: 'Playback speed',
  },
  es: {
    eyebrow: 'Minilección gratuita',
    steps: ['Escucha', 'Comprueba', 'Tu resultado'],
    lessonProgress: 'Progreso de la lección',
    listenPrompt: 'Escucha el episodio. Al llegar al 75 %, se desbloqueará la comprobación rápida.',
    continue: 'Continuar a la comprobación',
    keepListening: 'Sigue escuchando para desbloquear',
    question: 'Pregunta',
    chooseAnswer: 'Elige una respuesta',
    nextQuestion: 'Siguiente pregunta',
    seeResult: 'Ver resultado',
    resultEyebrow: 'Minilección completada',
    resultTitle: 'Has convertido la escucha en aprendizaje.',
    resultBody: 'Tu progreso está listo para guardarse. Crea una cuenta gratuita para desbloquear la siguiente lección de tu nivel.',
    score: 'correctas',
    saveProgress: 'Guarda tu progreso y desbloquea la siguiente lección',
    listenAgain: 'Escuchar de nuevo',
    playbackSpeed: 'Velocidad',
  },
  no: {
    eyebrow: 'Gratis minileksjon',
    steps: ['Lytt', 'Sjekk', 'Resultatet ditt'],
    lessonProgress: 'Leksjonsfremdrift',
    listenPrompt: 'Lytt til episoden. Når du har nådd 75 %, låses den raske sjekken opp.',
    continue: 'Fortsett til den raske sjekken',
    keepListening: 'Fortsett å lytte for å låse opp',
    question: 'Spørsmål',
    chooseAnswer: 'Velg ett svar',
    nextQuestion: 'Neste spørsmål',
    seeResult: 'Se resultatet',
    resultEyebrow: 'Minileksjonen er fullført',
    resultTitle: 'Du gjorde lytting om til læring.',
    resultBody: 'Fremdriften din er klar til å lagres. Opprett en gratis konto for å låse opp neste leksjon på nivået ditt.',
    score: 'riktige',
    saveProgress: 'Lagre fremdriften og lås opp neste leksjon',
    listenAgain: 'Lytt igjen',
    playbackSpeed: 'Avspillingshastighet',
  },
};

function normalizeDemoLanguage(language) {
  const normalized = String(language || '').trim().toLowerCase();
  if (normalized.startsWith('es')) return 'es';
  if (normalized.startsWith('no') || normalized.startsWith('nb') || normalized.startsWith('nn')) return 'no';
  return 'en';
}

function formatTime(seconds) {
  if (!Number.isFinite(seconds)) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

export default function HeroPodcastPanel({
  episode,
  compact = false,
  className = '',
  onRequestSignup,
  analyticsSource = 'homepage',
}) {
  const { t } = useTranslation('pages');
  const prefersReducedMotion = useReducedMotion();
  const audioRef = useRef(null);
  const demoStartedRef = useRef(false);
  const demoCompletedRef = useRef(false);
  const progressMilestonesRef = useRef(new Set());
  const resumeTimeRef = useRef(0);
  const demoId = episode?.episodeId || episode?.id || 'homepage_podcast';
  const language = normalizeDemoLanguage(episode?.language);
  const exercises = useMemo(() => getPublicDemoExercises(language), [language]);
  const copy = DEMO_COPY[language];
  const demoDescriptor = useMemo(() => ({
    demoId,
    language,
    level: episode?.level || '',
  }), [demoId, episode?.level, language]);
  const [demoState, setDemoState] = useState(() => createInitialPublicDemoState(demoDescriptor));
  const [selectedOptionId, setSelectedOptionId] = useState('');
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1);

  const analyticsContext = useMemo(() => ({
    demoId,
    source: analyticsSource,
    landingPage: typeof window !== 'undefined' ? window.location.pathname : '/',
    language,
    level: episode?.level,
  }), [analyticsSource, demoId, episode?.level, language]);

  const saveState = (nextState) => {
    if (typeof window !== 'undefined') {
      persistPublicDemoState(window.sessionStorage, nextState);
    }
    return nextState;
  };

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    if (episode?.audioUrl) {
      audio.src = episode.audioUrl;
      audio.load();
    }

    const restored = typeof window === 'undefined'
      ? createInitialPublicDemoState(demoDescriptor)
      : restorePublicDemoState(window.sessionStorage, demoDescriptor);

    setDemoState(restored);
    setSelectedOptionId('');
    setIsPlaying(false);
    setDuration(0);
    resumeTimeRef.current = restored.audioTime;
    demoStartedRef.current = restored.audioProgress > 0;
    demoCompletedRef.current = restored.completed;
    progressMilestonesRef.current = new Set(
      [25, 50, 75, 100].filter((milestone) => restored.audioProgress >= milestone),
    );
  }, [demoDescriptor, episode?.audioUrl]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.playbackRate = playbackRate;
    audio.defaultPlaybackRate = playbackRate;
  }, [episode?.audioUrl, playbackRate]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleLoadedMetadata = () => {
      setDuration(audio.duration);
      if (resumeTimeRef.current > 0 && Number.isFinite(audio.duration)) {
        audio.currentTime = Math.min(resumeTimeRef.current, Math.max(0, audio.duration - 0.1));
        resumeTimeRef.current = 0;
      }
    };

    const handleTimeUpdate = () => {
      if (!audio.duration) return;
      const nextProgress = Math.min(100, (audio.currentTime / audio.duration) * 100);

      setDemoState((current) => saveState({
        ...current,
        audioTime: audio.currentTime,
        audioProgress: Math.max(current.audioProgress, nextProgress),
      }));

      [25, 50, 75].forEach((milestone) => {
        if (nextProgress >= milestone && !progressMilestonesRef.current.has(milestone)) {
          progressMilestonesRef.current.add(milestone);
          trackDemoProgress(milestone, analyticsContext);
        }
      });
    };

    const handleEnded = () => {
      setIsPlaying(false);
      progressMilestonesRef.current.add(100);
      setDemoState((current) => saveState({
        ...current,
        audioTime: audio.duration || current.audioTime,
        audioProgress: 100,
        stage: current.completed ? 'result' : 'exercise',
      }));
      trackDemoProgress(100, analyticsContext);
    };

    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [analyticsContext]);

  const togglePlay = async () => {
    const audio = audioRef.current;
    if (!audio) return;

    try {
      if (isPlaying) {
        audio.pause();
        setIsPlaying(false);
        return;
      }

      if (audio.ended) audio.currentTime = 0;
      await audio.play();
      setIsPlaying(true);
      if (!demoStartedRef.current) {
        demoStartedRef.current = true;
        trackDemoStarted(analyticsContext);
      }
    } catch (error) {
      console.error('Podcast demo playback failed:', error);
    }
  };

  const skipBy = (seconds) => {
    const audio = audioRef.current;
    if (!audio || !Number.isFinite(audio.duration)) return;
    audio.currentTime = Math.max(0, Math.min(audio.duration, audio.currentTime + seconds));
  };

  const selectPlaybackRate = (rate) => {
    const audio = audioRef.current;
    if (audio) {
      audio.playbackRate = rate;
      audio.defaultPlaybackRate = rate;
    }
    setPlaybackRate(rate);
  };

  const beginExercises = () => {
    if (demoState.audioProgress < 75) return;
    audioRef.current?.pause();
    setIsPlaying(false);
    setDemoState((current) => saveState({ ...current, stage: 'exercise' }));
  };

  const selectDemoStage = (index) => {
    if (index === 0) {
      setDemoState((current) => saveState({ ...current, stage: 'listen' }));
      return;
    }

    if (index === 1 && demoState.audioProgress >= 75 && !demoState.completed) {
      beginExercises();
      return;
    }

    if (index === 2 && demoState.completed) {
      setDemoState((current) => saveState({ ...current, stage: 'result' }));
    }
  };

  const submitAnswer = () => {
    const question = exercises[demoState.currentQuestionIndex];
    if (!question || !selectedOptionId) return;

    const nextState = answerPublicDemoQuestion(
      demoState,
      question,
      selectedOptionId,
      exercises.length,
    );
    setDemoState(saveState(nextState));
    setSelectedOptionId('');

    if (nextState.completed && !demoCompletedRef.current) {
      demoCompletedRef.current = true;
      trackDemoCompleted(analyticsContext);
    }
  };

  const restartListening = () => {
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
    }

    const restartedState = createInitialPublicDemoState(demoDescriptor);
    setDemoState(saveState(restartedState));
    setSelectedOptionId('');
    setIsPlaying(false);
    resumeTimeRef.current = 0;
    demoStartedRef.current = false;
    demoCompletedRef.current = false;
    progressMilestonesRef.current = new Set();
  };

  const currentQuestion = exercises[demoState.currentQuestionIndex];
  const renderedDuration = duration > 0
    ? formatTime(duration)
    : (episode?.duration || '3:30');
  const lessonProgress = demoState.stage === 'result'
    ? 100
    : demoState.stage === 'exercise'
      ? 60 + ((demoState.currentQuestionIndex / exercises.length) * 40)
      : demoState.audioProgress * 0.6;
  const activeStep = demoState.stage === 'listen' ? 0 : demoState.stage === 'exercise' ? 1 : 2;

  const cardVariants = prefersReducedMotion
    ? {}
    : {
        initial: { opacity: 0, y: 28, scale: 0.985 },
        whileInView: { opacity: 1, y: 0, scale: 1 },
        viewport: { once: true, amount: 0.35 },
        transition: { duration: 0.65, ease: [0.22, 1, 0.36, 1] },
      };

  return (
    <motion.article
      className={`group relative overflow-hidden rounded-[2rem] border border-stone-200 bg-[linear-gradient(180deg,rgba(255,251,247,0.98),rgba(248,242,236,0.98))] shadow-[0_26px_60px_-34px_rgba(28,25,23,0.38)] ${className}`}
      {...cardVariants}
    >
      <audio ref={audioRef} preload="metadata" />

      <div className="pointer-events-none absolute inset-x-0 top-0 h-24 bg-[radial-gradient(circle_at_top,rgba(214,58,180,0.18),transparent_72%)]" />
      <div className="relative border-b border-stone-200/80 px-5 pb-4 pt-5 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between gap-4">
          <p className="text-[12px] font-semibold uppercase tracking-[0.18em] text-[#B2288E]">
            {copy.eyebrow}
          </p>
          <span className="text-xs font-semibold text-stone-500">{Math.round(lessonProgress)}%</span>
        </div>
        <div
          className="mt-3 h-1.5 overflow-hidden rounded-full bg-stone-200"
          role="progressbar"
          aria-label={copy.lessonProgress}
          aria-valuemin="0"
          aria-valuemax="100"
          aria-valuenow={Math.round(lessonProgress)}
        >
          <div
            className="h-full rounded-full bg-[#D63AB4] transition-[width] duration-500"
            style={{ width: `${lessonProgress}%` }}
          />
        </div>
        <ol className="mt-4 grid grid-cols-3 gap-2" aria-label={copy.lessonProgress}>
          {copy.steps.map((step, index) => {
            const accessible = index === 0
              || (index === 1 && demoState.audioProgress >= 75 && !demoState.completed)
              || (index === 2 && demoState.completed);
            const completedStep = index < activeStep;
            const activeStepClass = index === activeStep;

            return (
              <li key={step}>
                <button
                  type="button"
                  onClick={() => selectDemoStage(index)}
                  disabled={!accessible}
                  className={`flex min-h-9 w-full items-center justify-center gap-1.5 rounded-full border px-2 text-center text-[12px] font-semibold transition sm:text-[13px] ${
                    completedStep
                      ? 'border-[#D63AB4]/25 bg-[#D63AB4]/10 text-[#9C217E] hover:border-[#D63AB4]/45'
                      : activeStepClass
                        ? 'border-stone-900 bg-stone-900 text-white'
                        : accessible
                          ? 'border-stone-300 bg-white text-stone-700 hover:border-stone-900'
                          : 'cursor-not-allowed border-stone-200 bg-white/55 text-stone-400'
                  }`}
                >
                  {completedStep ? <Check size={14} aria-hidden="true" /> : null}
                  {!accessible && index > activeStep ? <Lock size={12} aria-hidden="true" /> : null}
                  <span>{step}</span>
                </button>
              </li>
            );
          })}
        </ol>
      </div>

      <div className={`relative grid gap-6 p-5 sm:p-6 ${compact ? '' : 'lg:grid-cols-[minmax(0,320px)_minmax(0,1fr)] lg:gap-8 lg:p-8'}`}>
        <div className={`relative overflow-hidden rounded-[1.75rem] bg-stone-900 ${compact ? 'aspect-[4/3]' : 'aspect-[4/5] max-h-[440px]'}`}>
          {episode?.artworkUrl ? (
            <img
              src={episode.artworkUrl}
              alt={episode.title || 'Podcast demo artwork'}
              className="h-full w-full object-cover"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-[radial-gradient(circle_at_top,#3f3f46,transparent_55%),linear-gradient(160deg,#1c1917,#312e2b)]">
              <img
                src="/new-magenta-favicon.svg"
                alt="Aprendos"
                width="112"
                height="112"
                className="w-28 rotate-[-12deg] sm:w-32"
              />
            </div>
          )}

          <div className="absolute inset-x-0 top-0 flex items-center justify-between p-4">
            <span className="rounded-full bg-white/88 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-stone-900 backdrop-blur-sm">
              {t('hero.demoCards.podcast.badge', 'Podcast')}
            </span>
            <span className="rounded-full bg-stone-950/60 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-white backdrop-blur-sm">
              {episode?.level || t('hero.player.levelFallback')}
            </span>
          </div>

          <div className="absolute inset-x-0 bottom-0 bg-[linear-gradient(180deg,transparent,rgba(0,0,0,0.82))] p-4 pt-14 text-white">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-white/72">
              {isPlaying ? t('hero.player.nowPlaying') : t('hero.player.preview', 'Preview')}
            </p>
            <h3
              className="mt-2 text-2xl leading-tight text-white sm:text-[2rem]"
              style={{ fontFamily: 'var(--font-display)' }}
            >
              {episode?.title || t('hero.player.loadingEpisode')}
            </h3>
            <p className="mt-2 max-w-[30ch] text-sm leading-6 text-white/72">
              {episode?.description || t('hero.demoCards.podcast.subtitle')}
            </p>
          </div>
        </div>

        <div className="flex min-w-0 flex-col justify-between rounded-[1.75rem] border border-white/70 bg-white/86 p-5 shadow-[inset_0_1px_0_rgba(255,255,255,0.78)] backdrop-blur-sm sm:p-6">
          {demoState.stage === 'listen' ? (
            <>
              <div>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#B2288E]">
                      {t('hero.listenHere', 'Listen Here')}
                    </p>
                    <h4
                      className="mt-2 text-[1.75rem] leading-tight text-stone-900 sm:text-[2rem]"
                      style={{ fontFamily: 'var(--font-display)' }}
                    >
                      {episode?.title || t('hero.demoCards.podcast.title')}
                    </h4>
                  </div>
                  <span className="rounded-full border border-stone-200 bg-stone-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-stone-500">
                    {renderedDuration}
                  </span>
                </div>

                <p className="mt-4 max-w-[52ch] text-[14px] leading-7 text-stone-600 sm:text-[15px]">
                  {episode?.playerDescription || episode?.description || t('hero.demoCards.podcast.subtitle')}
                </p>

                <div className="mt-8">
                  <div className="flex items-center justify-between text-xs font-semibold uppercase tracking-[0.14em] text-stone-500">
                    <span>{formatTime(demoState.audioTime)}</span>
                    <span>{renderedDuration}</span>
                  </div>
                  <div className="mt-3 h-2 rounded-full bg-stone-200/90">
                    <div
                      className="h-full rounded-full bg-[#D63AB4] transition-[width] duration-200"
                      style={{ width: `${demoState.audioProgress}%` }}
                    />
                  </div>
                  <p className="mt-4 text-[14px] font-medium leading-6 text-stone-600">
                    {copy.listenPrompt}
                  </p>
                </div>
              </div>

              <div className="mt-8 space-y-5">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => skipBy(-15)}
                      className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-stone-200 bg-white text-stone-600 transition hover:border-stone-900 hover:text-stone-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D63AB4] focus-visible:ring-offset-2"
                      aria-label={t('hero.player.rewindAria')}
                    >
                      <Rewind size={18} strokeWidth={1.8} />
                    </button>

                    <button
                      type="button"
                      onClick={togglePlay}
                      className="inline-flex h-14 min-w-14 items-center justify-center rounded-full bg-stone-900 px-5 text-white shadow-[0_16px_34px_-22px_rgba(28,25,23,0.7)] transition hover:-translate-y-0.5 hover:bg-stone-800 active:translate-y-0 active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D63AB4] focus-visible:ring-offset-2"
                      aria-label={isPlaying ? t('hero.player.pauseAria') : t('hero.player.playAria')}
                    >
                      {isPlaying ? <Pause size={22} fill="currentColor" /> : <Play size={22} fill="currentColor" className="ml-0.5" />}
                    </button>

                    <button
                      type="button"
                      onClick={() => skipBy(15)}
                      className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-stone-200 bg-white text-stone-600 transition hover:border-stone-900 hover:text-stone-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D63AB4] focus-visible:ring-offset-2"
                      aria-label={t('hero.player.skipAria')}
                    >
                      <FastForward size={18} strokeWidth={1.8} />
                    </button>
                  </div>

                  <div
                    className="flex items-center gap-2"
                    role="group"
                    aria-label={copy.playbackSpeed}
                  >
                    <span className="text-sm font-semibold text-stone-500">
                      {copy.playbackSpeed}
                    </span>
                    <div className="inline-flex rounded-full bg-stone-100 p-1">
                      {[1, 0.9, 0.75].map((rate) => {
                        const selected = playbackRate === rate;
                        return (
                          <button
                            key={rate}
                            type="button"
                            onClick={() => selectPlaybackRate(rate)}
                            aria-pressed={selected}
                            className={`min-h-9 rounded-full px-3 text-sm font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D63AB4] focus-visible:ring-offset-1 ${
                              selected
                                ? 'bg-stone-900 text-white shadow-sm'
                                : 'text-stone-600 hover:bg-white hover:text-stone-900'
                            }`}
                          >
                            {rate === 1 ? '1×' : `${rate.toFixed(2)}×`}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={beginExercises}
                  disabled={demoState.audioProgress < 75}
                  className="inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-full bg-[#D63AB4] px-5 py-3 text-[14px] font-semibold text-white transition hover:bg-[#B2288E] disabled:cursor-not-allowed disabled:bg-stone-200 disabled:text-stone-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D63AB4] focus-visible:ring-offset-2"
                >
                  {demoState.audioProgress >= 75 ? copy.continue : `${copy.keepListening} · ${Math.round(demoState.audioProgress)}%`}
                  <ChevronRight size={17} aria-hidden="true" />
                </button>
              </div>
            </>
          ) : null}

          {demoState.stage === 'exercise' && currentQuestion ? (
            <div className="flex h-full flex-col justify-between">
              <div>
                <p className="text-[12px] font-semibold uppercase tracking-[0.18em] text-[#B2288E]">
                  {copy.question} {demoState.currentQuestionIndex + 1} / {exercises.length}
                </p>
                <h4
                  className="mt-4 text-[1.8rem] leading-tight text-stone-950 sm:text-[2.2rem]"
                  style={{ fontFamily: 'var(--font-display)' }}
                >
                  {currentQuestion.prompt}
                </h4>
                <p className="mt-3 text-sm font-semibold text-stone-500">{copy.chooseAnswer}</p>

                <div className="mt-6 space-y-3" role="radiogroup" aria-label={currentQuestion.prompt}>
                  {currentQuestion.options.map((option) => {
                    const selected = selectedOptionId === option.id;
                    return (
                      <button
                        key={option.id}
                        type="button"
                        role="radio"
                        aria-checked={selected}
                        onClick={() => setSelectedOptionId(option.id)}
                        className={`flex min-h-14 w-full items-center gap-3 rounded-2xl border px-4 py-3 text-left text-[15px] font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D63AB4] focus-visible:ring-offset-2 ${
                          selected
                            ? 'border-[#D63AB4] bg-[#D63AB4]/8 text-stone-950'
                            : 'border-stone-200 bg-white text-stone-700 hover:border-stone-400'
                        }`}
                      >
                        <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full border ${selected ? 'border-[#D63AB4] bg-[#D63AB4] text-white' : 'border-stone-300'}`}>
                          {selected ? <Check size={14} aria-hidden="true" /> : null}
                        </span>
                        <span>{option.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <button
                type="button"
                onClick={submitAnswer}
                disabled={!selectedOptionId}
                className="mt-8 inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-full bg-stone-950 px-5 py-3 text-[14px] font-semibold text-white transition hover:bg-stone-800 disabled:cursor-not-allowed disabled:bg-stone-200 disabled:text-stone-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D63AB4] focus-visible:ring-offset-2"
              >
                {demoState.currentQuestionIndex < exercises.length - 1
                  ? copy.nextQuestion
                  : copy.seeResult}
                <ChevronRight size={17} aria-hidden="true" />
              </button>
            </div>
          ) : null}

          {demoState.stage === 'result' ? (
            <div className="flex h-full flex-col justify-between">
              <div>
                <CheckCircle2 size={42} strokeWidth={1.7} className="text-[#D63AB4]" aria-hidden="true" />
                <p className="mt-5 text-[12px] font-semibold uppercase tracking-[0.18em] text-[#B2288E]">
                  {copy.resultEyebrow}
                </p>
                <h4
                  className="mt-3 text-[2rem] leading-[1.08] text-stone-950 sm:text-[2.5rem]"
                  style={{ fontFamily: 'var(--font-display)' }}
                >
                  {copy.resultTitle}
                </h4>
                <div className="mt-6 inline-flex items-baseline gap-2 rounded-2xl border border-[#D63AB4]/20 bg-[#D63AB4]/8 px-5 py-3">
                  <strong className="text-3xl text-stone-950">{demoState.score}/{exercises.length}</strong>
                  <span className="text-sm font-semibold text-stone-600">{copy.score}</span>
                </div>
                <p className="mt-6 max-w-[48ch] text-[15px] leading-7 text-stone-600">
                  {copy.resultBody}
                </p>
              </div>

              <div className="mt-8 space-y-3">
                <button
                  type="button"
                  onClick={onRequestSignup}
                  className="inline-flex min-h-14 w-full items-center justify-center gap-2 rounded-full bg-[#D63AB4] px-5 py-3 text-center text-[14px] font-semibold text-white shadow-[0_16px_34px_-22px_rgba(178,40,142,0.72)] transition hover:-translate-y-0.5 hover:bg-[#B2288E] active:translate-y-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D63AB4] focus-visible:ring-offset-2"
                >
                  {copy.saveProgress}
                  <ChevronRight size={17} aria-hidden="true" />
                </button>
                <button
                  type="button"
                  onClick={restartListening}
                  className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-full px-4 text-[14px] font-semibold text-stone-600 transition hover:bg-stone-100 hover:text-stone-950 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D63AB4] focus-visible:ring-offset-2"
                >
                  <RotateCcw size={16} aria-hidden="true" />
                  {copy.listenAgain}
                </button>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </motion.article>
  );
}
